import React from 'react';
import ReactGA from 'react-ga4';

import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Spin } from 'antd';
import { isEmpty } from 'lodash';

import { IHotelDetails, IPackage, IRoomsContent } from '@common-types';
import { IAccount, IClientCash, IUserWallet } from '@share/common-types';
import { BlueButton } from '@share/components';
import { Currency, ModalCancellationPolicies } from '@components';
import { D_RESERVE_BUTTON } from '@share/constants';
import {
  RootState,
  renderNumber,
  getToken,
  LoginType,
  isCertificateApplied,
} from '@share/utils';
import {
  IHotelsState,
  ILoginState,
  IMarginatorState,
  hotelsActions,
  marginatorActions,
} from '@share/store/slices';
import {
  IRoomsSearchState,
  setSelectedHotelReviewClientCash,
  setSelectedHotelReviewClientCashStr,
} from '@store/slices';
import {
  AirSvg,
  BathSvg,
  BedSmallSvg,
  BedroomSvg,
  CarSvg,
  ClockSvg,
  ForkSvg,
  InfoSvg,
  KitchenSvg,
  ParkingSvg,
  PeopleSvg,
  PoolSvg,
  WifiSvg,
} from '@assets';
import {
  WalletPriceType,
  IWalletPriceSummary,
  applyMarginator,
  cleanVacationRentalAmenities,
  formatCheckTime,
  getBedroomLayout,
  getCertificateNavigationState,
  getHotelReserveUrl,
  getWalletPrices,
  groupBedTypes,
  parseSleepsCount,
  resolveReviewClientCash,
  summariseBathrooms,
} from '@utils';

import { HotelImagesSlider } from '../hotel-image-slider';
import { RoomsSearchPossibleStays } from '../rooms-search-possible-stays';

import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
  roomsSearchStore: IRoomsSearchState;
  hotelsStore: IHotelsState;
}

interface IMapDispatchToProps {
  setSelectedHotelReviewClientCash: (clientCash: number) => void;
  setSelectedHotelReviewClientCashStr: (selectedClientCash: string) => void;
  setSelectedHotelSearchClientCash: (clientCash: IClientCash) => void;
  setMarginatorCommission: (commission: number) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  account: IAccount;
  groupedPackages: IPackage[] | undefined;
  /**
   * The unit to render before availability answers. Everything the panel shows apart
   * from the price comes from the static call, so it is rendered straight away and
   * only the price column waits. Its presence means "no package yet, by design".
   */
  pendingRoomContent?: IRoomsContent;
  getRoomContent: (room: string) => IRoomsContent;
  nights: number;
  hotelId: number;
  hotelDetail: IHotelDetails;
  marginator: IMarginatorState;
  isStaticDetail?: boolean;
  certificateId?: string;
  certificateItemId?: string;
}

interface IState {
  isAllAmenitiesShown: boolean;
  isSliderVisible: boolean;
  sliderStartIndex: number;
  isCancellationPoliciesVisible: boolean;
}

const FIRST = 0;
const DECIMALS = 2;
const VISIBLE_AMENITIES = 8;
const HERO_PHOTOS = 5;
// Hero plus four thumbnails, i.e. what QuintessGallery shows at the top of the page.
const GALLERY_LEAD_COUNT = 5;
const PERCENT = 100;

const renderAmenityIcon = (amenity: string): React.ReactNode => {
  if (/wifi|internet/i.test(amenity)) {
    return <WifiSvg />;
  }
  if (/kitchen|refrigerator|microwave|dishwasher|oven|stove/i.test(amenity)) {
    return <KitchenSvg />;
  }
  if (/coffee|dining|breakfast|bar\b/i.test(amenity)) {
    return <ForkSvg />;
  }
  // Outdoor space of any kind shares the one outdoor icon the set has.
  if (/pool|hot tub|jacuzzi|sauna|beach|balcony|patio|terrace|deck|garden|yard/i.test(amenity)) {
    return <PoolSvg />;
  }
  if (/parking|garage/i.test(amenity)) {
    return <ParkingSvg />;
  }
  if (/car|shuttle|transport/i.test(amenity)) {
    return <CarSvg />;
  }
  if (/air condition|heating|fireplace|climate/i.test(amenity)) {
    return <AirSvg />;
  }
  if (/bath|shower|towel|toiletr/i.test(amenity)) {
    return <BathSvg size="18" />;
  }
  if (/bedroom/i.test(amenity)) {
    return <BedroomSvg size="18" />;
  }
  if (/bed|linen|sleep/i.test(amenity)) {
    return <BedSmallSvg size="18" />;
  }

  return <InfoSvg />;
};

class QuintessEntireHomeComponent extends React.Component<IProps, IState> {
  state: IState = {
    isAllAmenitiesShown: false,
    isSliderVisible: false,
    sliderStartIndex: FIRST,
    isCancellationPoliciesVisible: false,
  };

  get currentPackage(): IPackage | undefined {
    return this.props.groupedPackages?.[FIRST];
  }

  openSlider = (startIndex: number): void =>
    this.setState({ isSliderVisible: true, sliderStartIndex: startIndex });

  closeSlider = (): void => this.setState({ isSliderVisible: false });

  toggleAmenities = (): void =>
    this.setState(({ isAllAmenitiesShown }) => ({ isAllAmenitiesShown: !isAllAmenitiesShown }));

  showCancellationPolicies = (): void => this.setState({ isCancellationPoliciesVisible: true });

  hideCancellationPolicies = (): void => this.setState({ isCancellationPoliciesVisible: false });

  handleReserve = (marginatorCommission: number, walletPrices: IWalletPriceSummary): void => {
    const {
      account,
      history,
      hotelId,
      hotelsStore,
      isStaticDetail,
      certificateId,
      certificateItemId,
    } = this.props;

    const token = getToken(account);

    if (account?.type === LoginType.PrivateWithToken && !token) {
      return;
    }

    const packageId = this.currentPackage?.packageId as string;
    const { selectedHotelSearchClientCash } = hotelsStore;
    const selectedPropertyClientCash = resolveReviewClientCash(
      account,
      walletPrices,
      selectedHotelSearchClientCash?.selectedPropertyClientCash,
    );

    this.props.setMarginatorCommission(marginatorCommission);
    this.props.setSelectedHotelReviewClientCash(selectedPropertyClientCash as number);
    this.props.setSelectedHotelReviewClientCashStr(
      selectedPropertyClientCash ? selectedPropertyClientCash.toString() : '',
    );
    this.props.setSelectedHotelSearchClientCash({
      ...selectedHotelSearchClientCash,
      selectedPropertyReviewClientCash: selectedPropertyClientCash,
    } as IClientCash);

    history.push(
      getHotelReserveUrl(hotelId, packageId, history, isStaticDetail),
      getCertificateNavigationState(account, certificateId, certificateItemId),
    );

    ReactGA.event({
      category: account.name,
      action: `${D_RESERVE_BUTTON}_${account.name.toUpperCase()}`,
      label: 'User clicked reserve button on vacation rental detail',
      nonInteraction: false,
    });
  };

  renderFact = (icon: React.ReactNode, value: React.ReactNode, label: React.ReactNode) => (
    <li className="quintess-entire-home__fact">
      <span className="quintess-entire-home__fact-icon">{icon}</span>
      <span className="quintess-entire-home__fact-value">{value}</span>
      <span className="quintess-entire-home__fact-label">{label}</span>
    </li>
  );

  render(): React.ReactNode {
    const { isAllAmenitiesShown, isSliderVisible, sliderStartIndex, isCancellationPoliciesVisible } =
      this.state;
    const {
      account,
      getRoomContent,
      hotelDetail,
      loginStore,
      hotelsStore,
      roomsSearchStore,
      certificateId,
      marginator,
    } = this.props;

    const { pendingRoomContent } = this.props;
    const currentPackage = this.currentPackage;
    const isPricePending = !currentPackage && !!pendingRoomContent;

    if (!currentPackage && !isPricePending) {
      return null;
    }

    const {
      rooms = [],
      price,
      pricePerNight,
      currency = '',
      possibleStays = [],
      savings = 0,
      maxWalletClientCash = 0,
      cancellationPolicyText,
      refundabilityText,
    } = currentPackage || ({} as IPackage);

    const firstRoom = rooms[FIRST];
    const content = isPricePending
      ? pendingRoomContent
      : getRoomContent(firstRoom?.targetRoomKey as string);
    const certificateApplied = isCertificateApplied(account, certificateId);

    const stay = possibleStays[FIRST];
    const nights = stay?.days || this.props.nights;
    const unitPrice = possibleStays.length && !certificateApplied ? stay.price : price;

    const marginatorPrice = applyMarginator(unitPrice, marginator.value);
    const marginatorPricePerNight = applyMarginator(pricePerNight, marginator.value);
    const marginatorCommission = marginatorPrice - (unitPrice || 0);

    const { userWalletData } = loginStore;
    const { selectedHotelSearchClientCash } = hotelsStore;

    const walletPrices = getWalletPrices(
      account,
      userWalletData as IUserWallet,
      selectedHotelSearchClientCash,
      marginatorPrice,
      maxWalletClientCash,
      WalletPriceType.Details,
    );

    const displayWalletSavings =
      account?.walletWalletSavings &&
      (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) &&
      !!walletPrices?.maxClientCashAmountAllow;

    const total = certificateApplied
      ? unitPrice || 0
      : displayWalletSavings
        ? walletPrices?.priceFinalMax
        : walletPrices?.priceFinal;

    const amenities = cleanVacationRentalAmenities(content?.amenities, content?.bedTypes);
    const visibleAmenities = isAllAmenitiesShown ? amenities : amenities.slice(0, VISIBLE_AMENITIES);
    const bedGroups = groupBedTypes(content?.bedTypes);
    const bedroomLayout = getBedroomLayout(content?.bedroomDetails);
    const bathroomSummary = summariseBathrooms(content?.bathroomDetails);
    // Counted from the per-bedroom breakdown when there is one, so the fact card
    // cannot contradict the configuration listed directly beneath it; `bedTypes` is
    // the same information flattened, and the two do not always agree.
    const bedCount = bedroomLayout.length
      ? bedroomLayout.reduce(
          (total, { beds }) => total + beds.reduce((roomTotal, { count }) => roomTotal + count, 0),
          0,
        )
      : content?.bedTypes?.length || 0;
    // Capacity is only ever stated in prose. Deliberately no fallback to the
    // package's adultsCount: that is the party the guest searched for, not what
    // the home sleeps, and showing it as capacity misinforms.
    const sleeps =
      parseSleepsCount(content?.descriptions) ??
      parseSleepsCount(hotelDetail?.amenityDescriptions);

    const checkIn = formatCheckTime(hotelDetail?.checkInInformation?.[FIRST]);
    const checkOut = formatCheckTime(hotelDetail?.checkOutInformation?.[FIRST]);

    // Suppliers often leave the unit content without photos, so fall back to the
    // property set. Prefer the ones the top mosaic did not already lead with, to
    // keep this section a different slice of the gallery rather than a repeat.
    const unitImages = content?.images?.length
      ? content.images
      : (hotelDetail?.images || []).map(({ url }) => url);
    const beyondGalleryLead = unitImages.slice(GALLERY_LEAD_COUNT);
    const usesGalleryTail = beyondGalleryLead.length >= HERO_PHOTOS;
    // The strip is a slice, so the lightbox needs the matching offset to open on
    // the photo that was actually clicked.
    const photoOffset = usesGalleryTail ? GALLERY_LEAD_COUNT : FIRST;
    const photos = (usesGalleryTail ? beyondGalleryLead : unitImages).slice(0, HERO_PHOTOS);
    const savingsPercent = savings
      ? Math.round((savings / ((unitPrice || 0) + savings)) * PERCENT)
      : 0;

    const { error } = roomsSearchStore;

    return (
      <section className="quintess-entire-home">
        <header className="quintess-entire-home__header">
          <div>
            <h3 className="quintess-entire-home__title secondary-font">
              <FormattedMessage id="entire.home" />
            </h3>
            <p className="quintess-entire-home__subtitle">
              <FormattedMessage
                id={sleeps ? 'entire.home.summary' : 'entire.home.summary.rooms'}
                values={{
                  bedrooms: content?.nbrOfBedrooms || 0,
                  bathrooms: content?.nbrOfBathrooms || 0,
                  guests: sleeps || 0,
                }}
              />
            </p>
          </div>

          {savingsPercent > 0 && !account?.disableSavingsTagVacationRentals && (
            <span className="quintess-entire-home__savings">
              <FormattedMessage id="save.up.to" values={{ number: savingsPercent }} />
            </span>
          )}
        </header>

        {!!photos.length && (
          <div className="quintess-entire-home__photos">
            {photos.map((image, index) => (
              <button
                key={image}
                type="button"
                className="quintess-entire-home__photo"
                onClick={() => this.openSlider(index + photoOffset)}
              >
                <img src={image} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        <div className="quintess-entire-home__body">
          <div className="quintess-entire-home__main">
            <ul className="quintess-entire-home__facts">
              {!!content?.nbrOfBedrooms &&
                this.renderFact(
                  <BedroomSvg size="24" />,
                  content.nbrOfBedrooms,
                  <FormattedMessage id="bedrooms" />,
                )}
              {!!content?.nbrOfBathrooms &&
                this.renderFact(
                  <BathSvg size="24" />,
                  content.nbrOfBathrooms,
                  <FormattedMessage id="bathrooms" />,
                )}
              {!!sleeps &&
                this.renderFact(<PeopleSvg />, sleeps, <FormattedMessage id="guests" />)}
              {!!bedCount &&
                this.renderFact(
                  <BedSmallSvg size="24" />,
                  bedCount,
                  <FormattedMessage id="beds.label" />,
                )}
            </ul>

            {/* Per-bedroom where the supplier states it, falling back to the flat
                bed list for payloads that only carry `bedTypes`. */}
            {bedroomLayout.length ? (
              <div className="quintess-entire-home__section">
                <h4 className="quintess-entire-home__section-title">
                  <FormattedMessage id="bed.configuration" />
                </h4>
                <ul className="quintess-entire-home__bedrooms">
                  {bedroomLayout.map(({ name, beds }) => (
                    <li key={name} className="quintess-entire-home__bedroom">
                      <span className="quintess-entire-home__bedroom-name">
                        <BedroomSvg size="18" />
                        {name}
                      </span>
                      {!!beds.length && (
                        <span className="quintess-entire-home__bedroom-beds">
                          {beds.map(({ bedType, count }) => `${count} × ${bedType}`).join(' · ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              !!bedGroups.length && (
                <div className="quintess-entire-home__section">
                  <h4 className="quintess-entire-home__section-title">
                    <FormattedMessage id="bed.configuration" />
                  </h4>
                  <ul className="quintess-entire-home__beds">
                    {bedGroups.map(({ bedType, count }) => (
                      <li key={bedType} className="quintess-entire-home__bed">
                        <BedSmallSvg size="18" />
                        <span>
                          {count} × {bedType}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {!!bathroomSummary.length && (
              <div className="quintess-entire-home__section">
                <h4 className="quintess-entire-home__section-title">
                  <FormattedMessage id="bathroom.configuration" />
                </h4>
                <ul className="quintess-entire-home__beds">
                  {bathroomSummary.map(({ label, count }) => (
                    <li key={label} className="quintess-entire-home__bed">
                      <BathSvg size="18" />
                      <span>
                        {count} × {label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!amenities.length && (
              <div className="quintess-entire-home__section">
                <h4 className="quintess-entire-home__section-title">
                  <FormattedMessage id="whats.included" />
                </h4>
                <ul className="quintess-entire-home__amenities">
                  {visibleAmenities.map((amenity) => (
                    <li key={amenity} className="quintess-entire-home__amenity">
                      <span className="quintess-entire-home__amenity-icon">
                        {renderAmenityIcon(amenity)}
                      </span>
                      <span>{amenity}</span>
                    </li>
                  ))}
                </ul>

                {amenities.length > VISIBLE_AMENITIES && (
                  <button
                    type="button"
                    className="quintess-entire-home__link"
                    onClick={this.toggleAmenities}
                  >
                    {isAllAmenitiesShown ? (
                      <FormattedMessage id="show.less" />
                    ) : (
                      <FormattedMessage
                        id="show.all.amenities"
                        values={{ count: amenities.length }}
                      />
                    )}
                  </button>
                )}
              </div>
            )}

            {(!!checkIn || !!checkOut) && (
              <div className="quintess-entire-home__times">
                {!!checkIn && (
                  <div className="quintess-entire-home__time">
                    <ClockSvg />
                    <div>
                      <span className="quintess-entire-home__time-label">
                        <FormattedMessage id="check.in" />
                      </span>
                      <strong>{checkIn}</strong>
                    </div>
                  </div>
                )}
                {!!checkOut && (
                  <div className="quintess-entire-home__time">
                    <ClockSvg />
                    <div>
                      <span className="quintess-entire-home__time-label">
                        <FormattedMessage id="check.out" />
                      </span>
                      <strong>{checkOut}</strong>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="quintess-entire-home__price">
            {isPricePending && (
              <div className="quintess-entire-home__price-pending">
                <Spin />
                <span className="quintess-entire-home__price-pending-label">
                  <FormattedMessage id="checking.availability" />
                </span>
              </div>
            )}

            {!isPricePending && pricePerNight !== undefined && (
              <div className="quintess-entire-home__rate">
                <span className="quintess-entire-home__rate-amount">
                  <Currency currency={currency} />
                  {renderNumber(marginatorPricePerNight, DECIMALS)}
                </span>
                <span className="quintess-entire-home__rate-label">
                  <FormattedMessage id="per.night" />
                </span>
              </div>
            )}

            {!isPricePending && pricePerNight !== undefined && !!nights && (
              <ul className="quintess-entire-home__breakdown">
                <li>
                  <span>
                    <Currency currency={currency} />
                    {renderNumber(marginatorPricePerNight, DECIMALS)} ×{' '}
                    <FormattedMessage id="nights" values={{ count: nights }} />
                  </span>
                  <span>
                    <Currency currency={currency} />
                    {renderNumber(marginatorPrice, DECIMALS)}
                  </span>
                </li>
              </ul>
            )}

            {!isPricePending && (
              <div className="quintess-entire-home__total">
                <span>
                  <FormattedMessage id="total" />
                </span>
                <span className="quintess-entire-home__total-amount">
                  <Currency currency={currency} />
                  {renderNumber(total, DECIMALS)}
                </span>
              </div>
            )}

            <BlueButton
              disabled={isPricePending || !isEmpty(error)}
              className="btn btn-block w-100"
              onClick={() => this.handleReserve(marginatorCommission, walletPrices)}
            >
              <FormattedMessage id="reserve" />
            </BlueButton>

            {!isPricePending && !!cancellationPolicyText && (
              <button
                type="button"
                className="quintess-entire-home__link centered"
                onClick={this.showCancellationPolicies}
              >
                <FormattedMessage id="cancellation.policy" />
              </button>
            )}
          </aside>
        </div>

        <HotelImagesSlider
          onCancel={this.closeSlider}
          visible={isSliderVisible}
          images={unitImages.map((url: string) => ({
            description: 'property image',
            url,
            height: FIRST,
            width: FIRST,
          }))}
          hotelName={hotelDetail?.title}
          startIndex={sliderStartIndex}
        />

        <ModalCancellationPolicies
          cancellationPoliciesText={cancellationPolicyText as string}
          refundabilityText={refundabilityText as string}
          visible={isCancellationPoliciesVisible}
          onCancel={this.hideCancellationPolicies}
        />

        {!isPricePending && !certificateApplied && (
          <RoomsSearchPossibleStays
            hotelId={this.props.hotelId}
            possibleStays={possibleStays}
            groupedPackages={currentPackage}
            isVacationRentals
          />
        )}
      </section>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => ({
  loginStore: state.loginStore,
  roomsSearchStore: state.roomsSearchStore,
  hotelsStore: state.hotelsStore,
});

const mapDispatchToProps: IMapDispatchToProps = {
  setSelectedHotelReviewClientCash,
  setSelectedHotelReviewClientCashStr,
  setSelectedHotelSearchClientCash: hotelsActions.setSelectedHotelSearchClientCash,
  setMarginatorCommission: marginatorActions.setCommission,
};

export const QuintessEntireHome = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(QuintessEntireHomeComponent));
